import { memo, useMemo } from "react";

import {
    getZcLedger,
    useRaydiumLiquidityTransaction,
    getLoanStrategyIdentifier,
    getLoanRaydiumCollateral
} from "@bridgesplit/abf-react";
import { Result, LOADING_ERROR } from "@bridgesplit/utils";
import { AppButton } from "app/components/common";
import { useTransactionSender } from "app/components/transactions";
import { TxnManageRaydiumLiquidityParams } from "@bridgesplit/abf-sdk";

import { useRaydiumContext } from "../RaydiumContext";
import { useWithdrawLtv } from "./util";
import { getSafeSlippageDecimals } from "../util";

export default memo(function WithdrawCta() {
    const { withdrawForm, poolData, withdrawQuote } = useRaydiumContext();
    const removeLiquidity = useRemoveLiquidity();
    const withdrawLtv = useWithdrawLtv();

    const ctaError = useMemo(() => {
        if (!withdrawForm.percentToWithdraw) return "Enter an amount";
        if (withdrawForm.percentToWithdraw > 1) return "Invalid withdraw amount";

        if (!poolData || !withdrawQuote) return "Loading";

        if (withdrawForm.status === "error") return "No quote found";
        if (withdrawLtv.insufficientCollateral) return "Insufficient collateral";

        return undefined;
    }, [
        withdrawForm.percentToWithdraw,
        withdrawForm.status,
        poolData,
        withdrawQuote,
        withdrawLtv.insufficientCollateral
    ]);

    return (
        <AppButton
            asyncCta={{ onClickWithResult: removeLiquidity }}
            isTransaction
            color="secondary"
            disabled={!!ctaError}
        >
            {ctaError ?? "Withdraw"}
        </AppButton>
    );
});

function useRemoveLiquidity() {
    const { withdrawQuote, loanExpanded, reset, slippageController } = useRaydiumContext();
    const send = useTransactionSender();
    const removeLiquidity = useRaydiumLiquidityTransaction();

    return async () => {
        const zcLedger = getZcLedger(loanExpanded);

        if (!withdrawQuote || !zcLedger) return Result.errFromMessage(LOADING_ERROR);
        const strategyIdentifier = getLoanStrategyIdentifier(loanExpanded);
        if (!strategyIdentifier) return Result.errFromMessage("Updates are not available for this loan");

        const positionMint = getLoanRaydiumCollateral(loanExpanded)?.position.nftMint;
        if (!positionMint) return Result.errFromMessage("No position mint found");

        const slippageDecimals = getSafeSlippageDecimals(slippageController);
        const params: TxnManageRaydiumLiquidityParams = {
            loan: loanExpanded.loan.address,
            positionMint,
            increase: false,
            isTokenA: withdrawQuote.isTokenA,
            amount: withdrawQuote.amount,
            slippageDecimals,
            manageParams: {
                collateralIndex: 0,
                liquidityAmount: withdrawQuote.liquidity.toNumber(),
                manageParams: {
                    tokenAAmount: withdrawQuote.minTokenA.toNumber(),
                    tokenBAmount: withdrawQuote.minTokenB.toNumber()
                }
            }
        };

        return await send(removeLiquidity, params, { sendOptions: { onSuccess: reset }, preventAutoDialogClose: true });
    };
}
